import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Persists form state (a plain object) to localStorage under a stable key so
 * that accidental navigation away, browser refresh, or clicking outside a modal
 * doesn't wipe the user's work.
 *
 * Usage:
 *   const [formData, setFormData, draft] = useFormDraft(
 *     'newProjectForm',
 *     { name: '', client: '' },
 *     { enabled: dialogOpen && !editing }
 *   );
 *   // On modal open for a fresh form:
 *   draft.restoreIfAny();
 *   // On successful submit / explicit cancel:
 *   draft.clear();
 *
 * - `setFormData` behaves exactly like React's setState (value or updater fn).
 * - Auto-saves ~500 ms after the last edit while `enabled` is true.
 * - `draft.restoreIfAny()` re-reads the saved draft (call when opening a fresh form).
 * - `draft.clear()` wipes the localStorage entry and resets state to `initial`.
 * - `draft.hasDraft` becomes true whenever a saved draft is available in storage.
 */
export function useFormDraft(storageKey, initial, options = {}) {
  const { enabled = true, debounceMs = 500 } = options;
  const fullKey = `brillencrm:draft:${storageKey}`;

  const initialRef = useRef(initial);

  const readSaved = useCallback(() => {
    if (typeof window === "undefined") return null;
    try {
      const raw = window.localStorage.getItem(fullKey);
      if (!raw) return null;
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }, [fullKey]);

  const [data, setData] = useState(() => {
    const saved = readSaved();
    return saved ? { ...initialRef.current, ...saved } : initialRef.current;
  });
  const [hasDraft, setHasDraft] = useState(() => !!readSaved());

  // Debounced persistence
  const timerRef = useRef(null);
  useEffect(() => {
    if (!enabled) return undefined;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      try {
        // Skip persistence if the object is effectively empty (matches defaults).
        const isEmpty = Object.keys(data).every((k) => {
          const v = data[k];
          const def = initialRef.current[k];
          if (v === undefined || v === null || v === "") return true;
          if (Array.isArray(v)) {
            // Consider array "empty" if it matches the default array (by JSON compare).
            try {
              return JSON.stringify(v) === JSON.stringify(def ?? []);
            } catch {
              return v.length === 0;
            }
          }
          if (typeof v === "object") {
            try {
              return JSON.stringify(v) === JSON.stringify(def ?? {});
            } catch {
              return false;
            }
          }
          return v === def;
        });
        if (isEmpty) {
          window.localStorage.removeItem(fullKey);
          setHasDraft(false);
          window.dispatchEvent(new CustomEvent("brillencrm:draft-changed", { detail: { key: fullKey, hasDraft: false } }));
          return;
        }
        window.localStorage.setItem(fullKey, JSON.stringify(data));
        setHasDraft(true);
        window.dispatchEvent(new CustomEvent("brillencrm:draft-changed", { detail: { key: fullKey, hasDraft: true } }));
      } catch {
        /* ignore quota errors */
      }
    }, debounceMs);
    return () => timerRef.current && clearTimeout(timerRef.current);
  }, [data, enabled, fullKey, debounceMs]);

  const clear = useCallback(() => {
    try {
      window.localStorage.removeItem(fullKey);
    } catch {
      /* ignore */
    }
    setHasDraft(false);
    setData(initialRef.current);
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("brillencrm:draft-changed", { detail: { key: fullKey, hasDraft: false } }));
    }
  }, [fullKey]);

  const restoreIfAny = useCallback(() => {
    const saved = readSaved();
    if (saved) {
      setData({ ...initialRef.current, ...saved });
      setHasDraft(true);
      return true;
    }
    setHasDraft(false);
    return false;
  }, [readSaved]);

  return [data, setData, { clear, restoreIfAny, hasDraft }];
}
